import {
    defineStore,
} from 'pinia'
import {
    useContextStore,
} from '~~/stores/context'
import { PROVIDERS } from '#shared/providers'
import type { IamUserSmtpCredentials } from '~~/server/lib/aws/ses-smtp'
import type { IamPolicy, IamPolicyEntity, IamPolicyFinding, IamUserPolicy, IamUserPolicyAttachment, SesConfigurationSetDetail, SesIdentity, SesIdentityDetail } from '~~/server/lib/aws/aws'
import type {
    IamUserCredentials,
    StoredIamUserCredentials,
} from '~~/server/lib/aws/iam-user-credentials'
import type { SesReceiptRuleDetail, SesReceiptRuleSet, SesReceiptRuleSetDetail, SesSharedReceiptRule } from '~~/server/lib/aws/ses-receipt-rules'
import type { SnsTopic, SnsTopicDetail } from '~~/server/lib/aws/sns-topics'
import type { SnsSubscriptionDetail, SnsSubscriptionFilterPolicy, SnsTopicSubscription } from '~~/server/lib/aws/sns-subscriptions'
import type { SesEventDestination } from '~~/server/lib/aws/ses-configuration-set-event-destinations'
import type { SesEmailSetupInput, SesEmailSetupStatus } from '~~/server/lib/aws/ses-setup'
import type { EmailSendResult } from '~~/server/lib/aws/email-sender'
import type { IamUserTag } from '#shared/iam-user-tags'
import { ValidationError } from '~~/app/composables/validation'

export const DEFAULT_IAM_USERS_FILTER = 'mailer'

export type { IamPolicyFinding }

export interface CreateS3BucketValues {
    name: string
    acl: string
    objectLock: boolean
    blockPublicAccess: boolean
}

export type SesReceiptRuleSetListItem =Omit<SesReceiptRuleSet, 'createdOn'> & {
    createdOn: string,
}

export type SesReceiptRuleUpdateInput = Pick<SesReceiptRuleDetail, 'enabled' | 'tlsPolicy' | 'recipients' | 'scanEnabled' | 'actions'>

export type SesReceiptRuleSetDetailItem = Omit<SesReceiptRuleSetDetail, 'createdOn'> & {
    createdOn: string,
}

interface CreateSesIdentityInput {
    type: SesIdentity['type'],
    domainId: number,
    subdomain?: string,
    localPart?: string,
    configurationSetName: string,
}

interface CreateSesEventDestinationInput {
    name: string,
    enabled: boolean,
    eventTypes: string[],
    topicArn: string,
}

interface CreateSnsSubscriptionInput {
    protocol: string,
    endpoint: string,
    rawMessageDelivery: boolean,
}

export interface SesTestEmailInput {
    identity: string,
    localPart?: string,
    username: string,
    to: string,
    subject: string,
    text: string,
    html: string,
}

interface UpdateSesEventDestinationInput {
    enabled: boolean,
    eventTypes: string[],
    topicArn: string,
}

export const useAwsStore = defineStore('aws', () => {
    const iamUsers = ref([])
    const iamUsersFilter = ref(DEFAULT_IAM_USERS_FILTER)
    const iamUserCredentials = ref<StoredIamUserCredentials | null>(null)
    const iamUserSmtpCredentials = ref<IamUserSmtpCredentials | null>(null)
    const iamUserPolicies = ref<IamUserPolicy[]>([])
    const iamUserTags = ref<IamUserTag[]>([])
    const sesConfigurationSets = ref([])
    const sesIdentities = ref<SesIdentity[]>([])
    const sesIdentityList = ref<SesIdentity[]>([])
    const iamUsernames = ref<string[]>([])
    const sesReceiptRuleSets = ref<SesReceiptRuleSetListItem[]>([])
    const snsTopics = ref<SnsTopic[]>([])
    const snsTopicList = ref<SnsTopic[]>([])
    const snsTopic = ref<SnsTopicDetail | null>(null)
    const snsTopicSubscriptions = ref<Record<string, SnsTopicSubscription[]>>({})
    const snsTopicSubscriptionList = ref<SnsTopicSubscription[]>([])
    const snsSubscription = ref<SnsSubscriptionDetail | null>(null)
    const snsSubscriptionFilterPolicy = ref<SnsSubscriptionFilterPolicy | null>(null)
    const snsTopicPolicy = ref<object | null>(null)
    const snsTopicPolicies = ref<Record<string, object>>({})
    const sesConfigurationSetEventDestinations = ref<SesEventDestination[]>([])
    const sesIdentity =ref<SesIdentityDetail | null>(null)
    const sesConfigurationSet = ref<SesConfigurationSetDetail | null>(null)
    const sesReceiptRuleSet = ref<SesReceiptRuleSetDetailItem | null>(null)
    const sesReceiptRule = ref<SesReceiptRuleDetail | null>(null)
    const sesReceiptRuleSharedDomainRules = ref<SesSharedReceiptRule[]>([])
    const sesIamPolicies = ref<IamPolicy[]>([])
    const sesIamPolicy = ref<IamPolicy | null>(null)
    const sesIamPolicyEntities = ref<IamPolicyEntity[]>([])
    const sesIamPolicyDocument = ref<object | null>(null)
    const ec2Instances = ref([])
    const ec2InstancesInProgress = ref(new Set<string>())
    const selectedEc2Instances = ref([])
    const ec2InstanceInboundRules = ref([])
    const s3Buckets = ref([])
    const s3BucketPolicies = ref<Record<string, object | null>>({})

    const getIamUsers = computed(() => iamUsers.value)

    const contextStore = useContextStore()
    contextStore.registerProvider(PROVIDERS.AWS)

    const ctx = computed(() => contextStore.getContext(PROVIDERS.AWS))
    watch(ctx, (newCtx, oldCtx) => {
        if (newCtx?.identityKey !== oldCtx?.identityKey) {
            ec2Instances.value = []
            iamUsers.value = []
            iamUserSmtpCredentials.value = null
            iamUserPolicies.value = []
            iamUserTags.value = []
            s3Buckets.value = []
            sesConfigurationSets.value = []
            sesIdentities.value = []
            sesIdentityList.value = []
            iamUsernames.value = []
            sesReceiptRuleSets.value = []
            snsTopics.value = []
            snsTopicList.value = []
            snsTopic.value = null
            snsTopicSubscriptions.value = {}
            snsTopicSubscriptionList.value = []
            snsSubscription.value = null
            snsSubscriptionFilterPolicy.value = null
            snsTopicPolicy.value = null
            snsTopicPolicies.value = {}
            sesConfigurationSetEventDestinations.value = []
            sesIdentity.value = null
            sesConfigurationSet.value = null
            sesReceiptRuleSet.value = null
            sesReceiptRule.value = null
            sesReceiptRuleSharedDomainRules.value = []
            sesIamPolicies.value = []
            sesIamPolicy.value = null
            sesIamPolicyEntities.value = []
            sesIamPolicyDocument.value = null
        }
    })

    async function fetchIamUsers({ filter }: { filter?: string } = {}): Promise<void> {
        if (filter !== undefined) {
            iamUsersFilter.value = filter
        }
        const requestedFilter = iamUsersFilter.value
        const params = {}
        if (requestedFilter) {
            params['filter'] = requestedFilter
        }
        console.log('client fetchIamUsers params', params)
        useRequest({
            label: `fetch iam users`,
            fetch: () => $fetch(`/api/aws/iam/users`, {
                method: 'GET',
                params,
            }),
            evaluate: ({ users }) => [...users],
            store: (users): void => {
                if (requestedFilter === iamUsersFilter.value) {
                    iamUsers.value = users
                }
            },
            successDetails: (users) => users.length,
            indicateBusy: true,
        })
    }

    async function createIamUser({ projectSlug }): Promise<void> {
        await useRequest({
            label: `create iam user`,
            fetch: () => $fetch(`/api/aws/iam/users`, {
                method: 'POST',
                body: { projectSlug },
            }),
        })
    }

    async function fetchIamUserCredentials(username: string): Promise<void> {
        await useRequest({
            label: `fetch iam user credentials ${username}`,
            fetch: () => $fetch<StoredIamUserCredentials>(`/api/aws/iam/users/${username}/credentials`, {
                method: 'GET',
            }),
            store: (credentials) => iamUserCredentials.value = credentials,
            indicateBusy: true,
        })
    }

    async function saveIamUserCredentials(username: string, credentials: IamUserCredentials): Promise<void> {
        if (!credentials.accessKeyId || !credentials.secretAccessKey) {
            throw new ValidationError('Access key id and secret access key are required.')
        }

        await useRequest({
            label: `save iam user credentials ${username}`,
            fetch: () => $fetch(`/api/aws/iam/users/${username}/credentials`, {
                method: 'PUT',
                body: {
                    accessKeyId: credentials.accessKeyId,
                    secretAccessKey: credentials.secretAccessKey,
                },
            }),
            store: () => iamUserCredentials.value = {
                hasSecret: true,
                accessKeyId: credentials.accessKeyId,
                secretAccessKey: credentials.secretAccessKey,
            },
            indicateBusy: true,
        })
    }

    async function deleteIamUserCredentials(username: string, propagate = false): Promise<void> {
        await useRequest({
            label: `delete iam user credentials ${username}`,
            fetch: () => $fetch(`/api/aws/iam/users/${username}/credentials`, {
                method: 'DELETE',
            }),
            store: () => iamUserCredentials.value = {
                hasSecret: false,
            },
            propagate,
            indicateBusy: true,
        })
    }

    async function fetchIamUserSmtpCredentials(username: string): Promise<void> {
        await useRequest({
            label: `fetch iam user smtp credentials ${username}`,
            fetch: () => $fetch<IamUserSmtpCredentials>(`/api/aws/iam/users/${username}/smtp`, {
                method: 'GET',
            }),
            store: (credentials) => iamUserSmtpCredentials.value = credentials,
            indicateBusy: true,
        })
    }

    async function fetchIamUserPolicies(username: string): Promise<void> {
        await useRequest({
            label: `fetch iam user policies ${username}`,
            fetch: () => $fetch<{ policies: IamUserPolicy[] }>(`/api/aws/iam/users/${username}/policies`, {
                method: 'GET',
            }),
            evaluate: ({ policies }) => [...policies],
            store: (policies) => iamUserPolicies.value = policies,
            successDetails: (policies) => policies.length,
            indicateBusy: true,
        })
    }

    async function attachIamUserPolicy(username: string, attachment: IamUserPolicyAttachment): Promise<void> {
        await useRequest({
            label: `attach iam user ${attachment.type} ${username} ${attachment.arn}`,
            fetch: () => $fetch(`/api/aws/iam/users/${username}/policies`, {
                method: 'POST',
                body: attachment,
            }),
            propagate: true,
            indicateBusy: true,
        })
    }

    async function detachIamUserPolicy(username: string, attachment: IamUserPolicyAttachment): Promise<void> {
        await useRequest({
            label: `detach iam user ${attachment.type} ${username} ${attachment.arn}`,
            fetch: () => $fetch(`/api/aws/iam/users/${username}/policies`, {
                method: 'DELETE',
                body: attachment,
            }),
            propagate: true,
            indicateBusy: true,
        })
    }

    async function fetchIamUserTags(username: string): Promise<void> {
        await useRequest({
            label: `fetch iam user tags ${username}`,
            fetch: () => $fetch<{ tags: IamUserTag[] }>(`/api/aws/iam/users/${username}/tags`, {
                method: 'GET',
            }),
            evaluate: ({ tags }) => [...tags],
            store: (tags) => iamUserTags.value = tags,
            successDetails: (tags) => tags.length,
            indicateBusy: true,
        })
    }

    async function addIamUserTag(username: string, tag: IamUserTag): Promise<void> {
        await useRequest({
            label: `add iam user tag ${username} ${tag.key}`,
            fetch: () => $fetch(`/api/aws/iam/users/${username}/tags`, {
                method: 'POST',
                body: tag,
            }),
            propagate: true,
            indicateBusy: true,
        })
    }

    async function editIamUserTag(username: string, tag: IamUserTag): Promise<void> {
        await useRequest({
            label: `edit iam user tag ${username} ${tag.key}`,
            fetch: () => $fetch(`/api/aws/iam/users/${username}/tags/${encodeURIComponent(tag.key)}`, {
                method: 'PUT',
                body: { value: tag.value },
            }),
            propagate: true,
            indicateBusy: true,
        })
    }

    async function renameIamUserTag(username: string, key: string, newKey: string): Promise<void> {
        await useRequest({
            label: `rename iam user tag ${username} ${key}`,
            fetch: () => $fetch(`/api/aws/iam/users/${username}/tags/${encodeURIComponent(key)}/rename`, {
                method: 'POST',
                body: { key: newKey },
            }),
            propagate: true,
            indicateBusy: true,
        })
    }

    async function removeIamUserTag(username: string, key: string): Promise<void> {
        await useRequest({
            label: `remove iam user tag ${username} ${key}`,
            fetch: () => $fetch(`/api/aws/iam/users/${username}/tags/${encodeURIComponent(key)}`, {
                method: 'DELETE',
            }),
            propagate: true,
            indicateBusy: true,
        })
    }

    async function fetchSesConfigurationSets() {
        useRequest({
            label: `fetch ses configuration sets`,
            fetch: () => $fetch(`/api/aws/ses/configuration-sets`, {
                method: 'GET',
            }),
            evaluate: ({ configurationSets }) => [...configurationSets],
            store: (configurationSets) => sesConfigurationSets.value = configurationSets,
            successDetails: (configurationSets) => configurationSets.length,
        })
    }

    async function fetchSesIdentities(): Promise<void> {
        await useRequest({
            label: `fetch ses identities`,
            fetch: () => $fetch<{ identities: SesIdentity[] }>(`/api/aws/ses/identities`, {
                method: 'GET',
            }),
            evaluate: ({ identities }) => [...identities],
            store: (identities) => sesIdentities.value = identities,
            successDetails: (identities) => identities.length,
        })
    }

    async function fetchSesIdentityList(): Promise<void> {
        await useRequest({
            label: `fetch ses identity list`,
            fetch: () => $fetch<{ identities: SesIdentity[] }>(`/api/aws/ses/identities/list`, {
                method: 'GET',
            }),
            evaluate: ({ identities }) => [...identities],
            store: (identities) => sesIdentityList.value = identities,
            successDetails: (identities) => identities.length,
        })
    }

    async function fetchIamUsernames(): Promise<void> {
        await useRequest({
            label: `fetch iam usernames`,
            fetch: () => $fetch<{ users: { username: string }[] }>(`/api/aws/iam/users/list`, {
                method: 'GET',
            }),
            evaluate: ({ users }) => users.map((user): string => user.username),
            store: (usernames) => iamUsernames.value = usernames,
            successDetails: (usernames) => usernames.length,
        })
    }

    async function fetchSesReceiptRuleSets(): Promise<void> {
        await useRequest({
            label: `fetch ses receipt rule sets`,
            fetch: () => $fetch<{ ruleSets: SesReceiptRuleSetListItem[] }>(`/api/aws/ses/receipt-rule-sets`, {
                method: 'GET',
            }),
            evaluate: ({ ruleSets }) => [...ruleSets],
            store: (ruleSets) => sesReceiptRuleSets.value = ruleSets,
            successDetails: (ruleSets) => ruleSets.length,
        })
    }

    async function fetchSnsTopics(): Promise<void> {
        await useRequest({
            label: `fetch sns topics`,
            fetch: () => $fetch<{ topics: SnsTopic[] }>(`/api/aws/sns/topics`, {
                method: 'GET',
            }),
            evaluate: ({ topics }) => [...topics],
            store: (topics) => snsTopics.value = topics,
            successDetails: (topics) => topics.length,
            indicateBusy: true,
        })
    }

    async function fetchSnsTopic(name: string): Promise<void> {
        await useRequest({
            label: `fetch sns topic ${name}`,
            fetch: () => $fetch<SnsTopicDetail>(`/api/aws/sns/topics/${encodeURIComponent(name)}`, {
                method: 'GET',
            }),
            store: (topic) => snsTopic.value = topic,
            indicateBusy: true,
        })
    }

    async function fetchSnsTopicSubscriptions(name: string, key: string): Promise<void> {
        await useRequest({
            label: `fetch sns topic subscriptions ${name}`,
            fetch: () => $fetch<{ subscriptions: SnsTopicSubscription[] }>(`/api/aws/sns/topics/${encodeURIComponent(name)}/subscriptions`, {
                method: 'GET',
            }),
            evaluate: ({ subscriptions }) => [...subscriptions],
            store: (subscriptions) => snsTopicSubscriptions.value[key] = subscriptions,
            propagate: true,
        })
    }

    async function fetchSnsTopicSubscriptionList(name: string): Promise<void> {
        await useRequest({
            label: `fetch sns topic subscription list ${name}`,
            fetch: () => $fetch<{ subscriptions: SnsTopicSubscription[] }>(`/api/aws/sns/topics/${encodeURIComponent(name)}/subscriptions`, {
                method: 'GET',
            }),
            evaluate: ({ subscriptions }) => [...subscriptions],
            store: (subscriptions) => snsTopicSubscriptionList.value = subscriptions,
            indicateBusy: true,
        })
    }

    async function fetchSnsSubscription(name: string, id: string): Promise<void> {
        await useRequest({
            label: `fetch sns subscription ${name} ${id}`,
            fetch: () => $fetch<SnsSubscriptionDetail>(`/api/aws/sns/topics/${encodeURIComponent(name)}/subscriptions/${encodeURIComponent(id)}`, {
                method: 'GET',
            }),
            store: (subscription) => snsSubscription.value = subscription,
            propagate: true,
        })
    }

    function buildSnsSubscriptionFilterPolicyUrl(topic: string, id: string): string {
        return `/api/aws/sns/topics/${encodeURIComponent(topic)}/subscriptions/${encodeURIComponent(id)}/filter-policy`
    }

    async function fetchSnsSubscriptionFilterPolicy(topic: string, id: string): Promise<void> {
        await useRequest({
            label: `fetch sns subscription filter policy ${topic} ${id}`,
            fetch: () => $fetch<SnsSubscriptionFilterPolicy>(buildSnsSubscriptionFilterPolicyUrl(topic, id), {
                method: 'GET',
            }),
            store: (filterPolicy) => snsSubscriptionFilterPolicy.value = filterPolicy,
            indicateBusy: true,
            propagate: true,
        })
    }

    async function saveSnsSubscriptionFilterPolicy(topic: string, id: string, document: object): Promise<void> {
        await useRequest({
            label: `save sns subscription filter policy ${topic} ${id}`,
            fetch: () => $fetch(buildSnsSubscriptionFilterPolicyUrl(topic, id), {
                method: 'PUT',
                body: {
                    document,
                },
            }),
            propagate: true,
            indicateBusy: true,
        })
    }

    async function deleteSnsSubscriptionFilterPolicy(topic: string, id: string): Promise<void> {
        await useRequest({
            label: `delete sns subscription filter policy ${topic} ${id}`,
            fetch: () => $fetch(buildSnsSubscriptionFilterPolicyUrl(topic, id), {
                method: 'DELETE',
            }),
            propagate: true,
            indicateBusy: true,
        })
    }

    function clearSnsTopicSubscriptions(key: string): void {
        delete snsTopicSubscriptions.value[key]
    }

    async function deleteSnsTopic(name: string): Promise<void> {
        await useRequest({
            label: `delete sns topic ${name}`,
            fetch: () => $fetch(`/api/aws/sns/topics/${encodeURIComponent(name)}`, {
                method: 'DELETE',
            }),
            propagate: true,
        })
    }

    async function fetchSnsTopicPolicy(name: string): Promise<void> {
        await useRequest({
            label: `fetch sns topic policy ${name}`,
            fetch: () => $fetch<object>(`/api/aws/sns/topics/${encodeURIComponent(name)}/policy`, {
                method: 'GET',
            }),
            store: (document) => snsTopicPolicy.value = document,
            indicateBusy: true,
            propagate: true,
        })
    }

    async function fetchSnsTopicPolicyByName(name: string): Promise<void> {
        await useRequest({
            label: `fetch sns topic policy ${name}`,
            fetch: () => $fetch<object>(`/api/aws/sns/topics/${encodeURIComponent(name)}/policy`, {
                method: 'GET',
            }),
            store: (document) => snsTopicPolicies.value[name] = document,
            indicateBusy: true,
        })
    }

    async function validateSnsTopicPolicy(name: string, document: object): Promise<IamPolicyFinding[]> {
        return await useRequest({
            label: `validate sns topic policy ${name}`,
            fetch: () => $fetch<{ findings: IamPolicyFinding[] }>(`/api/aws/sns/topics/${encodeURIComponent(name)}/policy/validate`, {
                method: 'POST',
                body: {
                    document,
                },
            }),
            evaluate: ({ findings }) => findings,
            indicateBusy: true,
        }) as IamPolicyFinding[]
    }

    async function saveSnsTopicPolicy(name: string, document: object): Promise<void> {
        await useRequest({
            label: `save sns topic policy ${name}`,
            fetch: () => $fetch(`/api/aws/sns/topics/${encodeURIComponent(name)}/policy`, {
                method: 'PUT',
                body: {
                    document,
                },
            }),
            propagate: true,
            indicateBusy: true,
        })
    }

    async function fetchSnsTopicList(): Promise<void> {
        await useRequest({
            label: `fetch sns topic list`,
            fetch: () => $fetch<{ topics: SnsTopic[] }>(`/api/aws/sns/topics/list`, {
                method: 'GET',
            }),
            evaluate: ({ topics }) => [...topics],
            store: (topics) => snsTopicList.value = topics,
            successDetails: (topics) => topics.length,
        })
    }

    async function fetchSesConfigurationSetEventDestinations(name: string): Promise<void> {
        await useRequest({
            label: `fetch ses configuration set event destinations ${name}`,
            fetch: () => $fetch<{ eventDestinations: SesEventDestination[] }>(`/api/aws/ses/configuration-sets/${encodeURIComponent(name)}/event-destinations`, {
                method: 'GET',
            }),
            evaluate: ({ eventDestinations }) => [...eventDestinations],
            store: (eventDestinations) => sesConfigurationSetEventDestinations.value = eventDestinations,
            successDetails: (eventDestinations) => eventDestinations.length,
            indicateBusy: true,
        })
    }

    async function postSnsTopic(name: string): Promise<void> {
        await useRequest({
            label: `create sns topic ${name}`,
            fetch: () => $fetch(`/api/aws/sns/topics`, {
                method: 'POST',
                body: { name },
            }),
            propagate: true,
        })
    }

    async function createSnsTopic({ name }: { name: string }): Promise<void> {
        await postSnsTopic(name)
        await fetchSnsTopics()
    }

    async function createSnsTopicAndFetchList({ name }: { name: string }): Promise<void> {
        await postSnsTopic(name)
        await fetchSnsTopicList()
    }

    async function resubscribeSnsSubscription(topic: string, id: string): Promise<{ id: string, tracked: boolean }> {
        const result = await useRequest({
            label: `resubscribe sns subscription ${topic} ${id}`,
            fetch: () => $fetch<{ id: string, tracked: boolean }>(`/api/aws/sns/topics/${encodeURIComponent(topic)}/subscriptions/${encodeURIComponent(id)}/resubscribe`, {
                method: 'POST',
            }),
            propagate: true,
        })
        return result as { id: string, tracked: boolean }
    }

    async function createSnsSubscription(topic: string, values: CreateSnsSubscriptionInput): Promise<{ id: string, tracked: boolean }> {
        const result = await useRequest({
            label: `create sns subscription ${topic}`,
            fetch: () => $fetch<{ id: string, tracked: boolean }>(`/api/aws/sns/topics/${encodeURIComponent(topic)}/subscriptions`, {
                method: 'POST',
                body: values,
            }),
            propagate: true,
        })
        return result as { id: string, tracked: boolean }
    }

    async function createSesConfigurationSetEventDestination(name: string, values: CreateSesEventDestinationInput): Promise<void> {
        await useRequest({
            label: `create ses configuration set event destination ${values.name}`,
            fetch: () => $fetch(`/api/aws/ses/configuration-sets/${encodeURIComponent(name)}/event-destinations`, {
                method: 'POST',
                body: values,
            }),
            propagate: true,
        })
        await fetchSesConfigurationSetEventDestinations(name)
    }

    async function updateSesConfigurationSetEventDestination(configurationSet: string, eventDestination: string, values: UpdateSesEventDestinationInput): Promise<void> {
        const encodedConfigurationSet = encodeURIComponent(configurationSet)
        const encodedEventDestination = encodeURIComponent(eventDestination)
        await useRequest({
            label: `update ses configuration set event destination ${eventDestination}`,
            fetch: () => $fetch(`/api/aws/ses/configuration-sets/${encodedConfigurationSet}/event-destinations/${encodedEventDestination}`, {
                method: 'PUT',
                body: values,
            }),
            propagate: true,
        })
        await fetchSesConfigurationSetEventDestinations(configurationSet)
    }

    async function deleteSesConfigurationSetEventDestination(configurationSet: string, eventDestination: string): Promise<void> {
        const encodedConfigurationSet = encodeURIComponent(configurationSet)
        const encodedEventDestination = encodeURIComponent(eventDestination)
        await useRequest({
            label: `delete ses configuration set event destination ${eventDestination}`,
            fetch: () => $fetch(`/api/aws/ses/configuration-sets/${encodedConfigurationSet}/event-destinations/${encodedEventDestination}`, {
                method: 'DELETE',
            }),
            propagate: true,
        })
        await fetchSesConfigurationSetEventDestinations(configurationSet)
    }

    async function createSesReceiptRuleSet({ name }: { name: string }): Promise<void> {
        await useRequest({
            label: `create ses receipt rule set ${name}`,
            fetch: () => $fetch(`/api/aws/ses/receipt-rule-sets`, {
                method: 'POST',
                body: { name },
            }),
            propagate: true,
        })
        await fetchSesReceiptRuleSets()
    }

    async function createSesReceiptRule({ ruleSetName, name, domain }: {
        ruleSetName: string,
        name: string,
        domain: string,
    }): Promise<void> {
        await useRequest({
            label: `create ses receipt rule ${name}`,
            fetch: () => $fetch(`/api/aws/ses/receipt-rule-sets/${encodeURIComponent(ruleSetName)}/rules`, {
                method: 'POST',
                body: {
                    name,
                    domain,
                },
            }),
            propagate: true,
        })
    }

    async function deleteSesReceiptRule({ ruleSetName, name }: {
        ruleSetName: string,
        name: string,
    }): Promise<void> {
        await useRequest({
            label: `delete ses receipt rule ${name}`,
            fetch: () => $fetch(`/api/aws/ses/receipt-rule-sets/${encodeURIComponent(ruleSetName)}/rules/${encodeURIComponent(name)}`, {
                method: 'DELETE',
            }),
            propagate: true,
        })
    }

    async function saveSesReceiptRule({ ruleSetName, name, rule }: {
        ruleSetName: string,
        name: string,
        rule: SesReceiptRuleUpdateInput,
    }): Promise<void> {
        await useRequest({
            label: `save ses receipt rule ${name}`,
            fetch: () => $fetch(`/api/aws/ses/receipt-rule-sets/${encodeURIComponent(ruleSetName)}/rules/${encodeURIComponent(name)}`, {
                method: 'PUT',
                body: rule,
            }),
            propagate: true,
        })
    }

    async function activateSesReceiptRuleSet({ name }: { name: string }): Promise<void> {
        await useRequest({
            label: `activate ses receipt rule set ${name}`,
            fetch: () => $fetch(`/api/aws/ses/receipt-rule-sets/${encodeURIComponent(name)}/activate`, {
                method: 'POST',
            }),
            propagate: true,
        })
        await fetchSesReceiptRuleSets()
    }

    async function fetchSesReceiptRuleSet(name: string): Promise<void> {
        await useRequest({
            label: `fetch ses receipt rule set ${name}`,
            fetch: () => $fetch<SesReceiptRuleSetDetailItem>(`/api/aws/ses/receipt-rule-sets/${encodeURIComponent(name)}`, {
                method: 'GET',
            }),
            store: (ruleSet) => sesReceiptRuleSet.value = ruleSet,
        })
    }

    async function fetchSesReceiptRule(ruleSetName: string, name: string): Promise<void> {
        await useRequest({
            label: `fetch ses receipt rule ${name}`,
            fetch: () => $fetch<SesReceiptRuleDetail>(`/api/aws/ses/receipt-rule-sets/${encodeURIComponent(ruleSetName)}/rules/${encodeURIComponent(name)}`, {
                method: 'GET',
            }),
            store: (rule) => sesReceiptRule.value = rule,
        })
    }

    async function fetchSesReceiptRuleSharedDomainRules(ruleSetName: string, name: string): Promise<void> {
        await useRequest({
            label: `fetch ses receipt rule shared domain rules ${name}`,
            fetch: () => $fetch<SesSharedReceiptRule[]>(`/api/aws/ses/receipt-rule-sets/${encodeURIComponent(ruleSetName)}/rules/${encodeURIComponent(name)}/shared-domain-rules`, {
                method: 'GET',
            }),
            store: (rules) => sesReceiptRuleSharedDomainRules.value = rules,
            propagate: true,
        })
    }

    async function fetchSesIdentity(name: string): Promise<void> {
        await useRequest({
            label: `fetch ses identity ${name}`,
            fetch: () => $fetch<SesIdentityDetail>(`/api/aws/ses/identities/${encodeURIComponent(name)}`, {
                method: 'GET',
            }),
            store: (identity) => sesIdentity.value = identity,
        })
    }

    async function fetchSesConfigurationSet(name: string): Promise<void> {
        await useRequest({
            label: `fetch ses configuration set ${name}`,
            fetch: () => $fetch<SesConfigurationSetDetail>(`/api/aws/ses/configuration-sets/${encodeURIComponent(name)}`, {
                method: 'GET',
            }),
            store: (configurationSet) => sesConfigurationSet.value = configurationSet,
        })
    }

    async function deleteSesConfigurationSet(name: string): Promise<void> {
        await useRequest({
            label: `delete ses configuration set ${name}`,
            fetch: () => $fetch(`/api/aws/ses/configuration-sets/${encodeURIComponent(name)}`, {
                method: 'DELETE',
            }),
            propagate: true,
        })
    }

    async function fetchSesIamPolicies(): Promise<void> {
        await useRequest({
            label: `fetch ses iam policies`,
            fetch: () => $fetch<{ policies: IamPolicy[] }>(`/api/aws/iam/policies`, {
                method: 'GET',
            }),
            evaluate: ({ policies }) => [...policies],
            store: (policies) => sesIamPolicies.value = policies,
            successDetails: (policies) => policies.length,
            indicateBusy: true,
        })
    }

    async function fetchSesIamPolicy(arn: string): Promise<void> {
        await useRequest({
            label: `fetch ses iam policy ${arn}`,
            fetch: () => $fetch<{ policy: IamPolicy }>(`/api/aws/iam/policies/${encodeURIComponent(arn)}`, {
                method: 'GET',
            }),
            evaluate: ({ policy }) => policy,
            store: (policy) => sesIamPolicy.value = policy,
            indicateBusy: true,
        })
    }

    async function fetchSesIamPolicyEntities(arn: string): Promise<void> {
        await useRequest({
            label: `fetch ses iam policy entities ${arn}`,
            fetch: () => $fetch<{ entities: IamPolicyEntity[] }>(`/api/aws/iam/policies/${encodeURIComponent(arn)}/entities`, {
                method: 'GET',
            }),
            evaluate: ({ entities }) => entities,
            store: (entities) => sesIamPolicyEntities.value = entities,
            indicateBusy: true,
        })
    }

    async function fetchSesIamPolicyDocument(arn: string): Promise<void> {
        await useRequest({
            label: `fetch ses iam policy document ${arn}`,
            fetch: () => $fetch<{ document: object }>(`/api/aws/iam/policies/${encodeURIComponent(arn)}/document`, {
                method: 'GET',
            }),
            evaluate: ({ document }) => document,
            store: (document) => sesIamPolicyDocument.value = document,
            indicateBusy: true,
            propagate: true,
        })
    }

    async function createSesIamPolicy(name: string, propagate = false): Promise<string> {
        const trimmedName = name.trim()
        if (!trimmedName) {
            throw new ValidationError('Policy name is required.')
        }

        return await useRequest({
            label: `create ses iam policy ${trimmedName}`,
            fetch: () => $fetch<{ arn: string }>(`/api/aws/iam/policies`, {
                method: 'POST',
                body: {
                    name: trimmedName,
                },
            }),
            evaluate: ({ arn }) => arn,
            propagate,
            indicateBusy: true,
        }) as string
    }

    async function validateSesIamPolicyDocument(arn: string, document: object, propagate = false): Promise<IamPolicyFinding[]> {
        return await useRequest({
            label: `validate ses iam policy document ${arn}`,
            fetch: () => $fetch<{ findings: IamPolicyFinding[] }>(`/api/aws/iam/policies/${encodeURIComponent(arn)}/validate`, {
                method: 'POST',
                body: {
                    document,
                },
            }),
            evaluate: ({ findings }) => findings,
            propagate,
            indicateBusy: true,
        }) as IamPolicyFinding[]
    }

    async function saveSesIamPolicyDocument(arn: string, document: object, replaceOldestVersion: boolean, propagate = false): Promise<IamPolicyFinding[]> {
        return await useRequest({
            label: `save ses iam policy document ${arn}`,
            fetch: () => $fetch<{ findings: IamPolicyFinding[] }>(`/api/aws/iam/policies/${encodeURIComponent(arn)}/document`, {
                method: 'PUT',
                body: {
                    document,
                    replaceOldestVersion,
                },
            }),
            evaluate: ({ findings }) => findings,
            propagate,
            indicateBusy: true,
        }) as IamPolicyFinding[]
    }

    async function createSesIdentity({ type, domainId, subdomain, localPart, configurationSetName }: CreateSesIdentityInput): Promise<string> {
        return await useRequest({
            label: `create ses identity`,
            fetch: () => $fetch<{ name: string }>(`/api/aws/ses/identities`, {
                method: 'POST',
                body: {
                    type,
                    domainId,
                    subdomain,
                    localPart,
                    configurationSetName,
                },
            }),
            evaluate: ({ name }) => name,
            propagate: true,
        }) as string
    }

    async function createSesConfigurationSet({ projectSlug }) {
        useRequest({
            label: `create ses configuration set`,
            fetch: () => $fetch(`/api/aws/ses/configuration-sets`, {
                method: 'POST',
                body: { projectSlug },
            }),
        })
    }

    async function startSesEmailSetup(input: SesEmailSetupInput): Promise<number | undefined> {
        return await useRequest({
            label: `start ses email setup ${input.projectSlug}`,
            fetch: () => $fetch<{ flowId: number }>(`/api/aws/ses/email`, {
                method: 'POST',
                body: {
                    projectSlug: input.projectSlug,
                    domainId: input.domainId,
                    emailPrefix: input.emailPrefix,
                },
            }),
            evaluate: ({ flowId }) => flowId,
            indicateBusy: true,
        }) as number | undefined
    }

    async function sendSesTestEmail(input: SesTestEmailInput): Promise<EmailSendResult | undefined> {
        return await useRequest({
            label: `send ses test email ${input.identity}`,
            fetch: () => $fetch<{ result: EmailSendResult }>(`/api/aws/ses/email/test`, {
                method: 'POST',
                body: input,
            }),
            evaluate: ({ result }) => result,
            indicateBusy: true,
        }) as EmailSendResult | undefined
    }

    async function fetchSesEmailSetupStatus(flowId: number): Promise<SesEmailSetupStatus | undefined> {
        return await useRequest({
            label: `fetch ses email setup status ${flowId}`,
            fetch: () => $fetch<SesEmailSetupStatus>(`/api/aws/ses/email/${flowId}`, {
                method: 'GET',
            }),
        }) as SesEmailSetupStatus | undefined
    }

    async function fetchSubmittedRegions(providerOptionId: number | string): Promise<string[]> {
        return await useRequest({
            label: `fetch submitted aws regions for option ${providerOptionId}`,
            fetch: () => $fetch<string[]>(
                `/api/integration/admin/providers/aws/options/${providerOptionId}/submitted-values`,
            ),
            evaluate: (data: any) => Array.isArray(data) ? [...data] : [],
        }) as string[]
    }

    async function fetchEc2Instances() {
        useRequest({
            label: `fetch ec2 instances`,
            fetch: () => $fetch(`/api/aws/ec2/instances`, {
                method: 'GET',
            }),
            evaluate: ({ ec2Instances: i }) => [...i],
            store: (i) => ec2Instances.value = i,
            successDetails: (i) => i.length,
        })
    }

    async function startInstance({ instanceId }) {
        await changeInstanceState({ instanceId, action: 'start' })
    }

    async function stopInstance({ instanceId }) {
        await changeInstanceState({ instanceId, action: 'stop' })
    }

    async function changeInstanceState({ instanceId, action }) {
        ec2InstancesInProgress.value.add(instanceId)
        useRequest({
            label: `${action} instance ${instanceId}`,
            fetch: () => $fetch(`/api/aws/ec2/instances/${instanceId}/${action}`, {
                method: 'PUT',
            }),
            evaluate: ({ states }) => [...states],
            store: (states) => updateInstanceStates({ instanceId, newStates: states }),
        })
    }

    async function updateInstanceStates({ instanceId, newStates }) {
        newStates.forEach(async (newState) => {
            const instance = ec2Instances.value.find((oldState) => oldState.InstanceId === newState.instanceId)
            const state = newState.newState
            instance.State.Name = state
            if (!['running', 'stopped'].includes(state)) {
                setTimeout(async () => {
                    await pollInstanceState({ instanceId })
                }, 1000)
            }
            else {
                await useRequest({
                    label: `fetch single ec2 instance ${instanceId}`,
                    fetch: () => $fetch(`/api/aws/ec2/instances/${instanceId}`, {
                        method: 'GET',
                    }),
                    evaluate: ({ ec2Instance }) => ec2Instance,
                    store: (newInstance) => {
                        const index = ec2Instances.value.findIndex(i => i.InstanceId === newInstance.InstanceId)
                        if (index >= 0) {
                            ec2Instances.value[index] = newInstance
                        }
                        else {
                            ec2Instances.value.push(newInstance)
                        }
                    },
                    successDetails: (newInstance) => newInstance,
                    finalize: () => ec2InstancesInProgress.value.delete(instanceId),
                })
            }
        })
    }

    async function pollInstanceState({ instanceId }) {
        useRequest({
            label: `poll ec2 instance state ${instanceId}`,
            fetch: () => $fetch(`/api/aws/ec2/instances/${instanceId}/state`, {
                method: 'GET',
            }),
            evaluate: ({ states }) => [...states],
            store: (states) => updateInstanceStates({ instanceId, newStates: states }),
            successDetails: (states) => [states],
        })
    }

    async function fetchEc2InstanceInboundSecurityRules(instanceId) {
        useRequest({
            label: `fetching ec2 instance inbound security rules ${instanceId}`,
            fetch: () => $fetch(`/api/aws/ec2/instances/${instanceId}/security-groups/inbound-rules`, {
                method: 'GET',
            }),
            evaluate: ({ rules }) => [...rules],
            store: (rules) => ec2InstanceInboundRules.value = rules,
            successDetails: (rules) => rules.length,
        })
    }

    async function fetchS3Buckets() {
        return useRequest({
            label: `fetch s3 buckets`,
            fetch: () => $fetch(`/api/aws/s3/buckets`, {
                method: 'GET',
            }),
            evaluate: ({ s3Buckets: b }) => [...b],
            store: (b) => s3Buckets.value = b,
            successDetails: (b) => b.length,
            indicateBusy: true,
        })
    }

    async function fetchS3BucketPolicy(bucketName: string): Promise<void> {
        await useRequest({
            label: `fetch s3 bucket policy ${bucketName}`,
            fetch: () => $fetch<{ policy: object | null }>(`/api/aws/s3/buckets/${bucketName}/policy`, {
                method: 'GET',
            }),
            evaluate: ({ policy }) => policy,
            store: (policy) => s3BucketPolicies.value[bucketName] = policy,
            indicateBusy: true,
        })
    }

    async function fetchBucketsUsageInfo({ buckets }: { buckets: string[] }) {
        const result = await useRequest({
            label: `fetch s3 buckets usage info for buckets ${buckets}`,
            fetch: () => $fetch(`/api/aws/s3/buckets/usages`, {
                method: 'GET',
                params: { buckets },
            }),
        })

        const { s3BucketUsages } = result
        console.log('s3BucketUsages', s3BucketUsages)
        s3BucketUsages.forEach((usage) => {
            console.log('usage', usage)
            const match = s3Buckets.value.find((bucket) => bucket.Name === usage.bucket)
            console.log('match', match)
            if (match) {
                match.Size = usage.size
                match.HasMore = usage.hasMore
            }
        })
        console.log('fetchBucketsUsageInfo result', result)
    }

    async function createS3Bucket({ name, acl, objectLock, blockPublicAccess }: CreateS3BucketValues, propagate = false) {
        return await useRequest({
            label: `create s3 bucket`,
            fetch: () => $fetch(`/api/aws/s3/buckets`, {
                method: 'POST',
                body: { name, acl, objectLock, blockPublicAccess },
            }),
            propagate,
        })
    }

    async function createS3BucketPolicy(bucketName: string, document: object, propagate = false): Promise<void> {
        await useRequest({
            label: `create s3 bucket policy ${bucketName}`,
            fetch: () => $fetch(`/api/aws/s3/buckets/${bucketName}/policy`, {
                method: 'POST',
                body: { document },
            }),
            propagate,
        })
        await fetchS3BucketPolicy(bucketName)
    }

    function init() {}

    return {
        iamUsers,
        iamUsersFilter,
        iamUserCredentials,
        iamUserSmtpCredentials,
        iamUserPolicies,
        iamUserTags,
        sesConfigurationSets,
        sesIdentities,
        sesIdentityList,
        iamUsernames,
        sesReceiptRuleSets,
        snsTopics,
        snsTopicList,
        snsTopic,
        snsTopicSubscriptions,
        snsTopicSubscriptionList,
        snsSubscription,
        snsSubscriptionFilterPolicy,
        snsTopicPolicy,
        snsTopicPolicies,
        sesConfigurationSetEventDestinations,
        sesIdentity,
        sesConfigurationSet,
        sesReceiptRuleSet,
        sesReceiptRule,
        sesReceiptRuleSharedDomainRules,
        sesIamPolicies,
        sesIamPolicy,
        sesIamPolicyEntities,
        sesIamPolicyDocument,
        ec2Instances,
        ec2InstancesInProgress,
        selectedEc2Instances,
        ec2InstanceInboundRules,
        s3Buckets,
        s3BucketPolicies,
        getIamUsers,
        fetchIamUsers,
        createIamUser,
        fetchIamUserCredentials,
        saveIamUserCredentials,
        deleteIamUserCredentials,
        fetchIamUserSmtpCredentials,
        fetchIamUserPolicies,
        attachIamUserPolicy,
        detachIamUserPolicy,
        fetchIamUserTags,
        addIamUserTag,
        editIamUserTag,
        renameIamUserTag,
        removeIamUserTag,
        fetchSesConfigurationSets,
        fetchSesIdentities,
        fetchSesIdentityList,
        fetchIamUsernames,
        fetchSesReceiptRuleSets,
        fetchSnsTopics,
        fetchSnsTopicList,
        fetchSnsTopic,
        fetchSnsTopicSubscriptions,
        fetchSnsSubscription,
        fetchSnsSubscriptionFilterPolicy,
        saveSnsSubscriptionFilterPolicy,
        deleteSnsSubscriptionFilterPolicy,
        fetchSnsTopicSubscriptionList,
        clearSnsTopicSubscriptions,
        deleteSnsTopic,
        fetchSnsTopicPolicy,
        fetchSnsTopicPolicyByName,
        validateSnsTopicPolicy,
        saveSnsTopicPolicy,
        fetchSesConfigurationSetEventDestinations,
        createSnsTopic,
        createSnsTopicAndFetchList,
        createSnsSubscription,
        resubscribeSnsSubscription,
        createSesConfigurationSetEventDestination,
        updateSesConfigurationSetEventDestination,
        deleteSesConfigurationSetEventDestination,
        createSesReceiptRuleSet,
        createSesReceiptRule,
        deleteSesReceiptRule,
        saveSesReceiptRule,
        activateSesReceiptRuleSet,
        fetchSesReceiptRuleSet,
        fetchSesReceiptRule,
        fetchSesReceiptRuleSharedDomainRules,
        fetchSesIdentity,
        fetchSesConfigurationSet,
        deleteSesConfigurationSet,
        fetchSesIamPolicies,
        fetchSesIamPolicy,
        fetchSesIamPolicyEntities,
        fetchSesIamPolicyDocument,
        createSesIamPolicy,
        validateSesIamPolicyDocument,
        saveSesIamPolicyDocument,
        createSesIdentity,
        createSesConfigurationSet,
        startSesEmailSetup,
        sendSesTestEmail,
        fetchSesEmailSetupStatus,
        fetchSubmittedRegions,
        fetchEc2Instances,
        startInstance,
        stopInstance,
        changeInstanceState,
        updateInstanceStates,
        pollInstanceState,
        fetchEc2InstanceInboundSecurityRules,
        fetchS3Buckets,
        fetchS3BucketPolicy,
        fetchBucketsUsageInfo,
        createS3Bucket,
        createS3BucketPolicy,
        init,
    }
})
